import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from '@teetsh/app/src/utils/request';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { Evaluation } from '@teetsh/app/src/containers/CarnetsDeNotes/types/carnetsDeNotes';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { cdnKeys } from '@teetsh/app/src/containers/CarnetsDeNotes/queries/keys';
import { useTranslation } from 'react-i18next';

function deleteEvaluation({
  schoolyearId,
  schoolId,
  cdnId,
  evaluationId,
}: {
  schoolyearId: string;
  schoolId: string;
  cdnId: string;
  evaluationId: string;
}) {
  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/carnets-de-notes/${cdnId}/evaluations/${evaluationId}`;
  return request(requestURL, {
    method: 'DELETE',
  });
}

export function useDeleteEvaluation(carnetDeNotesId: string) {
  const { notifyError } = useNotification();
  const { schoolyear, school } = useSchoolSchoolyear();
  const { t } = useTranslation(['carnet']);
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationFn: (evaluationId: string) =>
      deleteEvaluation({
        schoolId: school.id,
        schoolyearId: schoolyear.id,
        cdnId: carnetDeNotesId,
        evaluationId,
      }),
    onMutate: evaluationId => {
      queryClient.setQueryData<Evaluation[]>(
        cdnKeys.cdnEvaluations(schoolyear.id, school.id, carnetDeNotesId),
        evals => (evals || []).filter(ev => ev.id !== evaluationId),
      );
    },
    onSuccess: (_, evaluationId) => {
      queryClient.invalidateQueries({
        queryKey: cdnKeys.cdnEvaluationDetails(
          schoolyear.id,
          school.id,
          carnetDeNotesId,
          evaluationId,
        ),
      });
      queryClient.invalidateQueries({
        queryKey: cdnKeys.cdnLimit(schoolyear.id, school.id, carnetDeNotesId),
      });
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: cdnKeys.cdnEvaluations(
          schoolyear.id,
          school.id,
          carnetDeNotesId,
        ),
      });
      notifyError({
        title: t('carnet:messages.evaluation.deleteError'),
      });
    },
  });
  return mutateAsync;
}
